"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { cn } from "@/lib/utils";

interface VideoDialogProps {
  isOpen: boolean;
  onClose: () => void;
  videoSrc: string;
}

export function VideoDialog({ isOpen, onClose, videoSrc }: VideoDialogProps) {
  const { t } = useTranslation();
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const backdropRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Sync internal state with prop
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
    }
  }, [isOpen]);

  // Blob URL workaround for seeking support in dev (Bun server range requests)
  useEffect(() => {
    if (isOpen && videoSrc) {
      setIsLoading(true);
      
      // Fetch the video and create a blob URL
      fetch(videoSrc)
        .then(res => res.blob())
        .then(blob => {
          const url = URL.createObjectURL(blob);
          setBlobUrl(url);
          setIsLoading(false);
        })
        .catch(err => {
          console.error("Failed to load video as blob:", err);
          setIsLoading(false);
          // Fallback to original src if blob fetch fails
          setBlobUrl(videoSrc);
        });
    }

    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
        setBlobUrl(null);
      }
    };
  }, [isOpen, videoSrc]);

  useGSAP(() => {
    if (isOpen) {
      const tl = gsap.timeline();
      
      tl.fromTo(backdropRef.current, 
        { opacity: 0 }, 
        { opacity: 1, duration: 0.3, ease: "power2.out" }
      );
      
      tl.fromTo(dialogRef.current, 
        { scale: 0.8, opacity: 0, y: 40 }, 
        { scale: 1, opacity: 1, y: 0, duration: 0.5, ease: "back.out(1.5)" },
        "-=0.2"
      );
    } else if (shouldRender) {
      const tl = gsap.timeline({
        onComplete: () => {
          setShouldRender(false);
        }
      });

      tl.to(dialogRef.current, {
        scale: 0.8,
        opacity: 0,
        y: 20,
        duration: 0.3,
        ease: "power2.in"
      });

      tl.to(backdropRef.current, {
        opacity: 0,
        duration: 0.3,
        ease: "power2.in"
      }, "-=0.1");
    }
  }, { scope: containerRef, dependencies: [isOpen] });

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when open
  useEffect(() => {
    if (shouldRender) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [shouldRender]);

  if (!shouldRender) return null;

  const dialogContent = (
    <div
      ref={containerRef}
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
    >
      {/* Backdrop */}
      <div 
        ref={backdropRef}
        className="absolute inset-0 z-0 bg-black/80 backdrop-blur-sm" 
        onClick={onClose}
      />

      {/* Dialog */}
      <div
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
        className={cn(
          "relative z-10 w-full max-w-5xl bg-black rounded-xl overflow-hidden shadow-2xl border border-white/10 aspect-video flex items-center justify-center",
        )}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 p-2 rounded-full bg-black/50 text-white/70 hover:text-white hover:bg-black/70 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
        </button>

        {isLoading ? (
          <div className="flex flex-col items-center gap-4 text-white/50">
            <div className="w-8 h-8 border-2 border-white/20 border-t-white/80 rounded-full animate-spin" />
            <p className="text-sm font-medium">{t("videoDetail.loadingVideoDialog")}</p>
          </div>
        ) : blobUrl && (
          <video
            ref={videoRef}
            src={blobUrl}
            autoPlay
            controls
            playsInline
            className="w-full h-full"
          />
        )}
      </div>
    </div>
  );

  return createPortal(dialogContent, document.body);
}
